import config from "@/config";
import { RelatedActivityType, RelatedContractType } from "@/shared/domain";

const CLAIM_SEGMENT = "/claim";
const INVOICE_SEGMENT = "/invoice";
const INSURED_GOOD_SEGMENT = "/insuredGood";
const CONTRACT_SEGMENT = "/contract";
const CONVERSATION_SEGMENT = "/conversation";
const ABOUT_SEGMENT = "/about";
const ACCESS_RIGHT_SEGMENT = "/accessRight";
const ACTIVITY_SEGMENT = "/activity";
const BROKER_SEGMENT = "/broker";
const INCIDENT_MESSAGE_SEGMENT = "/incidentMessage";
const TODO_SEGMENT = "/toDo";
const USER_SEGMENT = "/user";
const DATA_CHECK_SEGMENT = "/dataCheck";
const DOCUMENT_SEGMENT = "/document";
const POLICYHOLDER_SEGMENT = "/policyholder";
const REQUEST_SEGMENT = "/request";
const REFERENCE_SEGMENT = "/reference";
const VEHICLES_SEGMENT = "/vehicles";
const SEARCH_SEGMENT = "/search";
const PACKAGES_SEGMENT = "/packagestosign";

const REGEXP_PARAMETERS = /(\?.*)?$/;
const REGEXP_GUID = `[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}`;

const pattern = (regex1: RegExp, regex2: RegExp): RegExp => {
    const flags = (regex1.flags + regex2.flags)
        .split("")
        .sort()
        .join("")
        .replace(/(.)(?=.*\1)/g, "");
    return new RegExp(regex1.source + regex2.source, flags);
};

export class UrlManager {
    // SEARCH
    public static SEARCH_PATTERN = pattern(/^\/search/, REGEXP_PARAMETERS);
    // CLAIMS
    public static CLAIMS_PATTERN = pattern(/^\/claim/, REGEXP_PARAMETERS);
    public static ALL_CLAIMS_PATTERN = pattern(/^\/claim\/all/, REGEXP_PARAMETERS);
    public static ONGOING_CLAIMS_PATTERN = pattern(/^\/claim\/ongoing/, REGEXP_PARAMETERS);
    public static CLOSED_CLAIMS_PATTERN = pattern(/^\/claim\/closed/, REGEXP_PARAMETERS);
    public static RELATED_CLAIMS_PATTERN = pattern(/^\/claim\/(contract|insuredgood)\/.*/, REGEXP_PARAMETERS);
    public static CLAIM_PATTERN = pattern(/^\/claim\/\d*/, REGEXP_PARAMETERS);
    // INVOICES
    public static INVOICES_PATTERN = pattern(/^\/invoice/, REGEXP_PARAMETERS);
    public static INVOICE_CONTENT_PATTERN = pattern(/^\/invoice\/content/, REGEXP_PARAMETERS);
    public static INVOICE_PATTERN = pattern(/^\/invoice\/\d*/, REGEXP_PARAMETERS);
    // INSURED GOODS
    public static INSURED_GOODS_PATTERN = pattern(/^\/insuredGood/, REGEXP_PARAMETERS);
    public static INSURED_GOODS_PROFESSIONAL_PATTERN = pattern(/^\/insuredGood\/professional/, REGEXP_PARAMETERS);
    public static INSURED_GOOD_PATTERN = pattern(/^\/insuredGood\/\d*/, REGEXP_PARAMETERS);
    public static INSURED_GOODS_CATEGORY_COUNTERS_PATTERN = pattern(
        /^\/insuredGood\/category\/counters/,
        REGEXP_PARAMETERS,
    );
    public static INSURED_GOODS_FOR_POLICYHOLDER_PATTERN = pattern(
        /^\/insuredGood\/policyHolder\/\d*/,
        REGEXP_PARAMETERS,
    );
    public static POLICYHOLDERS_WITH_INSURED_GOODS_PATTERN = pattern(
        /^\/insuredGood\/policyholderList/,
        REGEXP_PARAMETERS,
    );
    // CONTRACTS
    public static CONTRACTS_PATTERN = pattern(/^\/contract/, REGEXP_PARAMETERS);
    public static CONTRACT_PATTERN = pattern(/^\/contract\/\d*/, REGEXP_PARAMETERS);
    // pattern similiar to the contract liability, then it is needed to negate the tenyearsliability endpoint
    public static RELATED_CONTRACTS_PATTERN = pattern(
        /^\/contract\/(?!.*(tenyearsliability))\w+\/\d+/,
        REGEXP_PARAMETERS,
    );
    public static INSURER_LOGO_PATTERN = /^\/contract\/insurerlogo\/\w*/;
    public static INSURER_LOGO_FILE_PATTERN = /^\/contract\/insurerlogofile\/\w*/;
    public static CONTRACTS_CATEGORY_COUNTERS_PATTERN = pattern(/^\/contract\/category\/counters/, REGEXP_PARAMETERS);
    public static CONTRACTS_SCHOOL_PATTERN = pattern(/^\/contract\/school/, REGEXP_PARAMETERS);
    public static CONTRACTS_COUNT_PATTERN = pattern(/^\/contract\/count/, REGEXP_PARAMETERS);
    public static CONTRACTS_LIABILITY_URL_PATTERN = pattern(/^\/contract\/tenyearsliability\/\d*/, REGEXP_PARAMETERS);
    // CONVERSATIONS
    public static CONVERSATIONS_PATTERN = pattern(/^\/conversation/, REGEXP_PARAMETERS);
    public static CONVERSATION_PATTERN = pattern(/^\/conversation\/\w*/, REGEXP_PARAMETERS);
    public static CONTEXTUAL_CONVERSATIONS_PATTERN = pattern(/^\/conversation\/\w*\/\w*/, REGEXP_PARAMETERS);
    public static CONVERSATION_POST_MESSAGE_PATTERN = pattern(/^\/conversation\/message\/\w*/, REGEXP_PARAMETERS);
    public static CREATE_CONVERSATION_PATTERN = pattern(/^\/conversation\/create/, REGEXP_PARAMETERS);
    // ToDo
    public static TODO_PATTERN = pattern(/^\/toDo/, REGEXP_PARAMETERS);
    // Incident message
    public static INCIDENT_MESSAGE_PATTERN = pattern(/^\/incidentMessage/, REGEXP_PARAMETERS);
    // About
    public static ABOUT_PATTERN = pattern(/^\/about/, REGEXP_PARAMETERS);
    // Access Right
    public static ACCESS_RIGHT_PATTERN = pattern(/^\/accessRight/, REGEXP_PARAMETERS);
    // Request Customer Card
    public static REQUEST_CUSTOMER_CARD_PATTERN = pattern(/^\/accessRight/, REGEXP_PARAMETERS);
    // Request Tariffication
    public static REQUEST_TARIFFICATION_PATTERN = pattern(/^\/request\/vehicle\/tariffication\/\d*/, REGEXP_PARAMETERS);
    // Request company data
    public static REQUEST_COMPANY_DATA_VERIFICATION_PATTERN = pattern(
        /^\/request\/policyholder\/moral\/details/,
        REGEXP_PARAMETERS,
    );
    public static COMPANY_DATA_VERIFICATION_PATTERN = pattern(
        /^\/policyholder\/\d*\/moral\/details/,
        REGEXP_PARAMETERS,
    );
    // Activities
    public static ACTIVITIES_PATTERN = pattern(/^\/activity\/\w*\/\w*/, REGEXP_PARAMETERS);
    // Broker
    public static BROKER_PATTERN = pattern(/^\/broker\/\d*/, REGEXP_PARAMETERS);
    public static BROKER_SETTINGS_PATTERN = pattern(/^\/broker\/settings/, REGEXP_PARAMETERS);
    public static BROKER_COLORS_PATTERN = pattern(/^\/broker\/\d*\/colors/, REGEXP_PARAMETERS);
    public static BROKERS_PATTERN = pattern(/^\/broker/, REGEXP_PARAMETERS);
    public static BROKER_SEARCH_PATTERN = pattern(/^\/broker\/fullTextSearch/, REGEXP_PARAMETERS);
    public static ADD_A_BROKER_PATTERN = pattern(/^\/broker/, REGEXP_PARAMETERS);
    // This one is used only by mock service
    public static DATA_CHECK_PATTERN = pattern(/^\/dataCheck\/.*/, REGEXP_PARAMETERS);
    public static CHECK_BANK_ACCOUNT_PATTERN = pattern(/^\/dataCheck\/bankAccount/, REGEXP_PARAMETERS);
    public static CHECK_BELGIAN_VAT_PATTERN = pattern(/^\/dataCheck\/belgianVAT/, REGEXP_PARAMETERS);
    public static CHECK_BCENUMBER_PATTERN = pattern(/^\/dataCheck\/bceNumber/, REGEXP_PARAMETERS);
    public static CHECK_UNIT_ESTABLISH_NUMBER_PATTERN = pattern(/^\/dataCheck\/unitEstablishNumber/, REGEXP_PARAMETERS);
    public static CHECK_EMAIL_PATTERN = pattern(/^\/dataCheck\/email/, REGEXP_PARAMETERS);
    public static CHECK_MATCH_VAT_BCE_PATTERN = pattern(/^\/dataCheck\/matchVatBce/, REGEXP_PARAMETERS);
    public static CHECK_PHONENUMBER_PATTERN = pattern(/^\/dataCheck\/phoneNumber/, REGEXP_PARAMETERS);
    // Request
    public static REQUESTS_PATTERN = pattern(/^\/request/, REGEXP_PARAMETERS);
    public static REQUESTS_SCHOOL_CLAIM_VICTIM_DETAILS_PATTERN = pattern(
        /^\/request\/schoolclaim\/victim/,
        REGEXP_PARAMETERS,
    );
    public static REQUEST_POLICYHOLDER_RELATION_PATTERN = pattern(
        /^\/request\/policyholder\/relation\/\d*/,
        REGEXP_PARAMETERS,
    );
    public static REQUEST_POLICYHOLDER_RELATION_DETAILS_PATTERN = pattern(
        /^\/request\/policyholder\/relation\/personalDetails\/\d*/,
        REGEXP_PARAMETERS,
    );
    public static REQUEST_POLICYHOLDER_DETAILS_PATTERN = pattern(
        /^\/request\/policyholder\/personalDetails\/\d*/,
        REGEXP_PARAMETERS,
    );
    public static REQUEST_POLICYHOLDER_ADDRESS_PATTERN = pattern(
        /^\/request\/policyholder\/address\/\d*/,
        REGEXP_PARAMETERS,
    );
    public static GREENCARD_PATTERN = pattern(/^\/request\/greencard\/\w*/, REGEXP_PARAMETERS);
    public static CLAIM_REPORT_PATTERN = pattern(/^\/request\/claimreport/, REGEXP_PARAMETERS);
    public static SCHOOL_CLAIM_PATTERN = pattern(/^\/request\/schoolclaim/, REGEXP_PARAMETERS);
    public static LIABILITY_FORM_PATTERN = pattern(/^\/request\/tenyearsliability\/\w*/, REGEXP_PARAMETERS);
    public static GET_SALARY_FORM_PATTERN = pattern(/^\/request\/salaryselfemployed\/\w*/, REGEXP_PARAMETERS);
    public static POST_SALARY_FORM_PATTERN = pattern(/^\/request\/salaryselfemployed/, REGEXP_PARAMETERS);
    public static REGISTER_SIGNUP_PATTERN = pattern(/^\/user\/register\/signup/, REGEXP_PARAMETERS);
    public static REGISTER_TOKEN_VALIDATION_PATTERN = pattern(/^\/user\/register\/token-validation/, REGEXP_PARAMETERS);
    public static REGISTER_COMPLETE_PATTERN = pattern(/^\/user\/register\/complete/, REGEXP_PARAMETERS);
    public static REGISTER_RESEND_EMAIL_PATTERN = pattern(/^\/user\/register\/resendemail/, REGEXP_PARAMETERS);
    public static AUTHENTICATE_PATTERN = pattern(/^\/user\/authenticate/, REGEXP_PARAMETERS);
    public static AUTHENTICATE_SCOPED_PATTERN = pattern(/^\/user\/authenticate\/scoped/, REGEXP_PARAMETERS);
    public static MAIL_STATUS_PATTERN = pattern(/^\/user\/mail\/status/, REGEXP_PARAMETERS);
    public static USER_STATUS_PATTERN = pattern(/^\/user\/status/, REGEXP_PARAMETERS);
    public static RESET_PWD_INIT_PATTERN = pattern(/^\/user\/resetpassword\/init/, REGEXP_PARAMETERS);
    public static RESET_PWD_VERIFY_GUID_PATTERN = pattern(
        new RegExp(`/user/resetpassword/${REGEXP_GUID}`),
        REGEXP_PARAMETERS,
    );
    public static RESET_PWD_COMPLETE_PATTERN = pattern(/^\/user\/resetpassword\/complete/, REGEXP_PARAMETERS);
    public static ITSME_INFO_PATTERN = pattern(/^\/user\/itsme\/info/, REGEXP_PARAMETERS);
    public static ITSME_SIGNUP_INIT_PATTERN = pattern(/^\/user\/itsme\/signup\/init/, REGEXP_PARAMETERS);
    public static ITSME_REGISTER_PATTERN = pattern(/^\/user\/itsme\/register/, REGEXP_PARAMETERS);
    public static ITSME_SIGNUP_COMPLETE_PATTERN = pattern(/^\/user\/itsme\/signup\/complete/, REGEXP_PARAMETERS);
    public static ITSME_DELETE_PATTERN = pattern(/^\/user\/itsme\/delete\/\w*/, REGEXP_PARAMETERS);
    public static ACTIVATE_TOKEN_VALIDATION_PATTERN = pattern(/^\/user\/activate\/token-validation/, REGEXP_PARAMETERS);
    public static ACTIVATE_ITSME_TOKEN_VALIDATION_PATTERN = pattern(
        /^\/user\/itsme\/activate\/token-validation/,
        REGEXP_PARAMETERS,
    );
    public static ACTIVATE_COMPLETE_PATTERN = pattern(/^\/user\/activate\/complete/, REGEXP_PARAMETERS);
    public static ITSME_ACTIVATE_COMPLETE_PATTERN = pattern(/^\/user\/itsme\/activate\/complete/, REGEXP_PARAMETERS);

    // Data check
    public static POLICYHOLDER_PATTERN = pattern(/^\/policyholder/, REGEXP_PARAMETERS);
    public static POLICYHOLDER_PERSONAL_DETAILS_PATTERN = pattern(
        /^\/policyholder\/personalDetails\/\d*/,
        REGEXP_PARAMETERS,
    );
    public static POLICYHOLDER_ADDRESS_PATTERN = pattern(/^\/policyholder\/address\/\d*/, REGEXP_PARAMETERS);
    public static RELATION_PERSONAL_DETAILS_PATTERN = pattern(
        /^\/policyholder\/relation\/personalDetails\/\d*/,
        REGEXP_PARAMETERS,
    );
    public static POLICYHOLDER_RELATION_PATTERN = pattern(/^\/policyholder\/relation\/\d*/, REGEXP_PARAMETERS);
    // Policyholder Mandates
    public static POLICYHOLDER_MANDATES_PATTERN = pattern(/^\/policyholder\/\d*\/mandates/, REGEXP_PARAMETERS);
    // Document
    public static DOCUMENTS_PATTERN = pattern(/^\/document/, REGEXP_PARAMETERS);
    public static SEARCH_DOCUMENTS_PATTERN = pattern(/^\/document\/search/, REGEXP_PARAMETERS);
    public static RELATED_DOCUMENTS_PATTERN = pattern(/^\/document\/related/, REGEXP_PARAMETERS);
    public static DOCUMENTS_PACKAGES_PATTERN = pattern(/^\/document\/packagetosign/, REGEXP_PARAMETERS);
    public static DOCUMENTS_PACKAGES_URL_PATTERN = pattern(/^\/document\/packagetosign\/.*\/url/, REGEXP_PARAMETERS);
    public static RELATED_DOCUMENT_PATTERN = pattern(/^\/document\/related\/\d*/, REGEXP_PARAMETERS);
    public static POLICYHOLDER_RELATED_DOCUMENT_PATTERN = pattern(
        /^\/document\/related\/policyholder\/\d*/,
        REGEXP_PARAMETERS,
    );
    public static LINKED_DOCUMENTS_PATTERN = pattern(/^\/document\/\w*(\/\w*)?/, REGEXP_PARAMETERS);
    public static DOCUMENT_CONTENT_PATTERN = pattern(/^\/document\/content\/\d*/, REGEXP_PARAMETERS);
    public static SIGN_DOCUMENT_PATTERN = pattern(/^\/document\/sign/, REGEXP_PARAMETERS);
    // REFERENCES
    public static WINDSHIELD_PHONES_REFERENCE_PATTERN = pattern(/^\/reference\/windshieldrepairers/, REGEXP_PARAMETERS);
    // VEHICLES
    public static VEHICLE_INFO = pattern(/^\/vehicles\/\w*/, REGEXP_PARAMETERS);
    public static VEHICLES_BRAND_PATTERN = pattern(/^\/vehicles\/brands/, REGEXP_PARAMETERS);
    public static VEHICLES_MODELS_PATTERN = pattern(/^\/vehicles\/brands\/\w*\/models/, REGEXP_PARAMETERS);
    public static VEHICLES_FUEL_TYPE_PATTERN = pattern(/^\/vehicles\/fueltype/, REGEXP_PARAMETERS);
    public static VEHICLES_INFO_PATTERN = pattern(/^\/vehicles\/brands\/\w*\/models\/\w*/, REGEXP_PARAMETERS);
    public static POLICYHOLDER_VEHICLES_PATTERN = pattern(/^\/vehicles\/policyholder\/\d*/, REGEXP_PARAMETERS);
    public static VEHICLES_COVERAGE_PATTERN = pattern(/^\/vehicles\/coverage/, REGEXP_PARAMETERS);
    // PACKAGES TO SIGN
    public static PACKAGES_TO_SIGN_CONTENT_PATTERN = pattern(/^\/packagestosign\/content/, REGEXP_PARAMETERS);

    public static search(): string {
        return SEARCH_SEGMENT;
    }

    public static claims(): string {
        return CLAIM_SEGMENT;
    }

    public static filteredClaims(category: string): string {
        return `${CLAIM_SEGMENT}/${category}`;
    }

    public static relatedClaims(category: "contract" | "insuredgood", id: string): string {
        return `${CLAIM_SEGMENT}/${category}/${id}`;
    }

    public static claim(id: number): string {
        return `${CLAIM_SEGMENT}/${id}`;
    }

    public static invoices(): string {
        return INVOICE_SEGMENT;
    }

    public static invoiceContent(): string {
        return `${INVOICE_SEGMENT}/content`;
    }

    public static invoice(id: number): string {
        return `${INVOICE_SEGMENT}/${id}`;
    }

    public static insuredGoods(): string {
        return INSURED_GOOD_SEGMENT;
    }

    public static insuredGoodsProfessional(): string {
        return `${INSURED_GOOD_SEGMENT}/professional`;
    }

    public static insuredGood(id: number): string {
        return `${INSURED_GOOD_SEGMENT}/${id}`;
    }

    public static insuredgoodsCategoryCounters(): string {
        return `${INSURED_GOOD_SEGMENT}/category/counters`;
    }

    public static insuredGoodsForPolicyholder(policyholderId: number): string {
        return `${INSURED_GOOD_SEGMENT}/policyHolder/${policyholderId}`;
    }

    public static policyholderWithInsuredGoods(): string {
        return `${INSURED_GOOD_SEGMENT}/policyholderList`;
    }

    public static contracts(): string {
        return CONTRACT_SEGMENT;
    }

    public static contract(id: number): string {
        return `${CONTRACT_SEGMENT}/${id}`;
    }

    public static relatedContracts(id: string | number, featureType: RelatedContractType): string {
        return `${CONTRACT_SEGMENT}/${featureType}/${id}`;
    }

    // User

    public static insurerLogo(id: string): string {
        return `${CONTRACT_SEGMENT}/insurerlogo/${id}`;
    }

    public static contractsCategoryCounters(): string {
        return `${CONTRACT_SEGMENT}/category/counters`;
    }

    public static contractsSchool(): string {
        return `${CONTRACT_SEGMENT}/school`;
    }

    public static contractsCount(): string {
        return `${CONTRACT_SEGMENT}/count`;
    }

    public static contractsLiabilityUrl(policyholderId: number): string {
        return `${CONTRACT_SEGMENT}/tenyearsliability/${policyholderId}`;
    }

    public static conversations(): string {
        return CONVERSATION_SEGMENT;
    }

    public static conversation(id: string): string {
        return `${CONVERSATION_SEGMENT}/${id}`;
    }

    public static contextualConversations(category: "party" | "risk" | "contract" | "damage", id: string): string {
        return `${CONVERSATION_SEGMENT}/${category}/${id}`;
    }

    public static conversationPostMessage(id: string): string {
        return `${CONVERSATION_SEGMENT}/message/${id}`;
    }

    public static createConversation(policyholderId?: number): string {
        const policyholderIdParam = policyholderId ? `?policyholderId=${policyholderId}` : "";
        return `${CONVERSATION_SEGMENT}/create${policyholderIdParam}`;
    }

    public static toDo(): string {
        return TODO_SEGMENT;
    }

    public static incidentMessage(): string {
        return INCIDENT_MESSAGE_SEGMENT;
    }

    public static about(): string {
        return ABOUT_SEGMENT;
    }

    public static accessRight(): string {
        return ACCESS_RIGHT_SEGMENT;
    }

    public static requestCustomerCard(): string {
        return `${REQUEST_SEGMENT}/customercard`;
    }

    public static requestTariffication(policyholderId: number | string): string {
        return `${REQUEST_SEGMENT}/vehicle/tariffication/${policyholderId}`;
    }

    public static requestCompanyDataVerification(): string {
        return `${REQUEST_SEGMENT}/policyholder/moral/details`;
    }

    public static companyDataVerification(policyholderId: string): string {
        return `${POLICYHOLDER_SEGMENT}/${policyholderId}/moral/details`;
    }

    public static activities(category: RelatedActivityType, id: number | string): string {
        return `${ACTIVITY_SEGMENT}/${category}/${id}`;
    }

    public static broker(officeId: string): string {
        return `${BROKER_SEGMENT}/${officeId}`;
    }

    public static brokerSettings(): string {
        return `${BROKER_SEGMENT}/settings`;
    }

    public static brokerColors(officeId: string): string {
        return `${BROKER_SEGMENT}/${officeId}/colors`;
    }

    public static brokers(): string {
        return BROKER_SEGMENT;
    }

    public static searchBroker(): string {
        return `${BROKER_SEGMENT}/fullTextSearch`;
    }

    public static addABroker(): any {
        return BROKER_SEGMENT;
    }

    public static checkBankAccount(): string {
        return `${DATA_CHECK_SEGMENT}/bankAccount`;
    }

    public static checkBelgianVAT(): string {
        return `${DATA_CHECK_SEGMENT}/belgianVAT`;
    }

    public static checkBceNumber(): string {
        return `${DATA_CHECK_SEGMENT}/bceNumber`;
    }

    public static checkUnitEstablishNumber(): string {
        return `${DATA_CHECK_SEGMENT}/unitEstablishNumber`;
    }

    public static checkEmail(): string {
        return `${DATA_CHECK_SEGMENT}/email`;
    }

    public static checkMatchVatBce(): string {
        return `${DATA_CHECK_SEGMENT}/matchVatBce`;
    }

    public static checkPhoneNumber(): string {
        return `${DATA_CHECK_SEGMENT}/phoneNumber`;
    }

    public static requests(): string {
        return REQUEST_SEGMENT;
    }

    public static schoolClaimVictimDetails(): string {
        return `${REQUEST_SEGMENT}/schoolclaim/victim`;
    }

    public static requestPolicyholderRelation(policyholderId: number): string {
        return `${REQUEST_SEGMENT}/policyholder/relation/${policyholderId}`;
    }

    public static requestPolicyholderRelationDetails(policyholderId: number): string {
        return `${REQUEST_SEGMENT}/policyholder/relation/personalDetails/${policyholderId}`;
    }

    public static requestPolicyholderDetails(policyholderId: number): string {
        return `${REQUEST_SEGMENT}/policyholder/personalDetails/${policyholderId}`;
    }

    public static requestPolicyholderAddress(policyholderId: number): string {
        return `${REQUEST_SEGMENT}/policyholder/address/${policyholderId}`;
    }

    public static greenCard(vehicleId: number): string {
        return `${REQUEST_SEGMENT}/greencard/${vehicleId}`;
    }

    public static claimReport(): string {
        return `${REQUEST_SEGMENT}/claimreport`;
    }

    public static schoolClaim(): string {
        return `${REQUEST_SEGMENT}/schoolclaim`;
    }

    public static liabilityForm(policyholderId: number): string {
        return `${REQUEST_SEGMENT}/tenyearsliability/${policyholderId}`;
    }

    public static getSalaryForm(policyholderId: number): string {
        return `${REQUEST_SEGMENT}/salaryselfemployed/${policyholderId}`;
    }

    public static postSalaryForm(): string {
        return `${REQUEST_SEGMENT}/salaryselfemployed`;
    }

    public static registerSignup(): string {
        return `${USER_SEGMENT}/register/signup`;
    }

    public static registerTokenValidation(): string {
        return `${USER_SEGMENT}/register/token-validation`;
    }

    public static registerComplete(): string {
        return `${USER_SEGMENT}/register/complete`;
    }

    public static registerResendEmail(): string {
        return `${USER_SEGMENT}/register/resendemail`;
    }

    public static authenticate(): string {
        return `${USER_SEGMENT}/authenticate`;
    }

    public static authenticateScoped(): string {
        return `${USER_SEGMENT}/authenticate/scoped`;
    }

    public static mailStatus(): string {
        return `${USER_SEGMENT}/mail/status`;
    }

    public static userStatus(): string {
        return `${USER_SEGMENT}/status`;
    }

    public static resetPasswordInit(): string {
        return `${USER_SEGMENT}/resetpassword/init`;
    }

    public static resetPasswordVerifyGuid(guid: string): string {
        return `${USER_SEGMENT}/resetpassword/${guid}`;
    }

    public static resetPasswordComplete(): string {
        return `${USER_SEGMENT}/resetpassword/complete`;
    }

    public static itsmeInfo(): string {
        return `${USER_SEGMENT}/itsme/info`;
    }

    public static itsmeSignupInit(): string {
        return `${USER_SEGMENT}/itsme/signup/init`;
    }

    public static itsmeRegister(): string {
        return `${USER_SEGMENT}/itsme/register`;
    }

    public static itsmeSignupComplete(): string {
        return `${USER_SEGMENT}/itsme/signup/complete`;
    }

    public static itsmeDelete(itsMeUid: string): string {
        return `${USER_SEGMENT}/itsme/delete/${itsMeUid}`;
    }

    public static activateTokenValidation(): string {
        return `${USER_SEGMENT}/activate/token-validation`;
    }

    public static activateItsmeTokenValidation(): string {
        return `${USER_SEGMENT}/itsme/activate/token-validation`;
    }

    public static activateComplete(): string {
        return `${USER_SEGMENT}/activate/complete`;
    }

    public static itsmeActivateComplete(): string {
        return `${USER_SEGMENT}/itsme/activate/complete`;
    }

    public static policyholder(): string {
        return `${POLICYHOLDER_SEGMENT}`;
    }

    public static policyholderPersonalDetails(policyholderId: number): string {
        return `${POLICYHOLDER_SEGMENT}/personalDetails/${policyholderId}`;
    }

    public static policyholderAddress(policyholderId: number): string {
        return `${POLICYHOLDER_SEGMENT}/address/${policyholderId}`;
    }

    public static relationPersonalDetails(relationDetailsId: number): string {
        return `${POLICYHOLDER_SEGMENT}/relation/personalDetails/${relationDetailsId}`;
    }

    public static policyholderRelation(id: number): string {
        return `${POLICYHOLDER_SEGMENT}/relation/${id}`;
    }

    public static policyholderMandates(policyholderId: number): string {
        return `${POLICYHOLDER_SEGMENT}/${policyholderId}/mandates`;
    }

    public static documents(): string {
        return DOCUMENT_SEGMENT;
    }

    public static searchDocuments(): string {
        return `${DOCUMENT_SEGMENT}/search`;
    }

    public static relatedDocuments(): string {
        return `${DOCUMENT_SEGMENT}/related`;
    }

    public static documentsPackages(): string {
        return `${DOCUMENT_SEGMENT}/packagetosign`;
    }

    public static documentsPackageUrl(id: string): string {
        return `${DOCUMENT_SEGMENT}/packagetosign/${id}/url`;
    }

    public static relatedDocument(id: string): string {
        return `${DOCUMENT_SEGMENT}/related/${id}`;
    }

    public static policyholderRelatedDocument(id: string): string {
        return `${DOCUMENT_SEGMENT}/related/policyholder/${id}`;
    }

    public static linkedDocuments(category: "insuredgood" | "contract" | "claim", id?: number | string): string {
        if (id) {
            return `${DOCUMENT_SEGMENT}/${category}/${id}`;
        }
        return `${DOCUMENT_SEGMENT}/${category}`;
    }

    public static documentContent(id: number): string {
        return `${DOCUMENT_SEGMENT}/content/${id}`;
    }

    public static signDocument(): string {
        return `${DOCUMENT_SEGMENT}/sign`;
    }

    public static referenceWindshieldPhones(): string {
        return `${REFERENCE_SEGMENT}/windshieldrepairers`;
    }

    public static vehicleInfo(vehicleId: string): string {
        return `${VEHICLES_SEGMENT}/${vehicleId}`;
    }

    public static vehiclesBrands(): string {
        return `${VEHICLES_SEGMENT}/brands`;
    }

    public static vehiclesModels(brand: string): string {
        return `${VEHICLES_SEGMENT}/brands/${brand}/models`;
    }

    public static vehiclesFuelType(): string {
        return `${VEHICLES_SEGMENT}/fueltype`;
    }

    public static vehiclesInfo(brand: string, model: string): string {
        return `${VEHICLES_SEGMENT}/brands/${brand}/models/${model}`;
    }

    public static policyholderVehicles(policyholderId: number | string): string {
        return `${VEHICLES_SEGMENT}/policyholder/${policyholderId}`;
    }

    public static vehiclesCoverage(): string {
        return `${VEHICLES_SEGMENT}/coverage`;
    }

    public static packagesToSignContent(): string {
        return `${PACKAGES_SEGMENT}/content`;
    }

    public static brokerSchema(brokerId: string): string {
        return process.env.STORYBOOK_ENV
            ? "/sp/broker/scheme.css"
            : `${config.SPAPI_URL}/api/broker/${brokerId}/scheme`;
    }

    public static brokerLogo(brokerId: string): string {
        return process.env.STORYBOOK_ENV ? "/sp/broker/logo.png" : `${config.SPAPI_URL}/api/broker/logo/${brokerId}`;
    }

    public static brokerLogoSmall(brokerId: string): string {
        return process.env.STORYBOOK_ENV
            ? "/sp/broker/logo.png"
            : `${config.SPAPI_URL}/api/broker/logosmall/${brokerId}`;
    }
}
